import type { ButtonHTMLAttributes, ReactNode } from 'react'
import { AnimatePresence, motion } from 'motion/react'
import { Check } from 'lucide-react'
import { useMostrarExtras } from '../lib/preferencias'

/** La cabecera de cada pantalla: antetítulo, título en Fraunces y una línea. */
export function Cabecera({
  titulo,
  subtitulo,
  children,
}: {
  titulo: string
  subtitulo?: ReactNode
  children?: ReactNode
}) {
  return (
    <header className="flex items-end justify-between gap-3">
      <div className="min-w-0">
        <p className="label-caps text-primary">Recetario de Lucía</p>
        <h1 className="mt-1.5 font-display text-4xl leading-none first-letter:uppercase">{titulo}</h1>
        {subtitulo && <p className="mt-2 text-sm text-muted-foreground">{subtitulo}</p>}
      </div>
      {children && <div className="flex shrink-0 items-center gap-1">{children}</div>}
    </header>
  )
}

/** Tarjeta de fondo claro con bordes amplios y sin sombras duras. */
export function Tarjeta({ className = '', children }: { className?: string; children: ReactNode }) {
  return <section className={`rounded-3xl border border-border/70 bg-card p-4 ${className}`}>{children}</section>
}

type Variante = 'principal' | 'secundario' | 'peligro'

const VARIANTES: Record<Variante, string> = {
  principal: 'bg-primary text-primary-foreground hover:opacity-90',
  secundario: 'border border-border bg-card text-foreground hover:border-primary/40 hover:text-primary',
  peligro: 'border border-destructive/30 bg-card text-destructive hover:bg-destructive/10',
}

export function Boton({
  variante = 'secundario',
  ancho = false,
  className = '',
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { variante?: Variante; ancho?: boolean }) {
  return (
    <button
      type="button"
      {...props}
      className={`inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl px-5 text-sm font-semibold outline-none transition-[opacity,colors,transform] focus-visible:ring-2 focus-visible:ring-ring active:scale-[0.98] disabled:pointer-events-none disabled:opacity-45 ${VARIANTES[variante]} ${ancho ? 'w-full' : ''} ${className}`}
    />
  )
}

/** Botón redondo con icono, para las flechas y los ajustes de la cabecera. */
export function BotonIcono({
  label,
  className = '',
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { label: string }) {
  return (
    <button
      type="button"
      aria-label={label}
      {...props}
      className={`grid h-11 w-11 shrink-0 place-items-center rounded-full border border-border bg-card text-foreground outline-none transition-colors hover:border-primary/40 hover:text-primary focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-40 ${className}`}
    />
  )
}

/** La casilla de "hecho": se rellena de salvia con un pequeño rebote. */
export function Casilla({
  marcada,
  label,
  onClick,
}: {
  marcada: boolean
  label: string
  onClick: () => void
}) {
  return (
    <motion.button
      type="button"
      aria-label={label}
      aria-pressed={marcada}
      onClick={onClick}
      whileTap={{ scale: 0.88 }}
      className={`grid h-11 w-11 shrink-0 place-items-center rounded-full outline-none focus-visible:ring-2 focus-visible:ring-ring`}
    >
      <span
        className={`grid h-7 w-7 place-items-center rounded-[10px] border-2 transition-colors duration-200 ${
          marcada ? 'border-sage bg-sage text-card' : 'border-border bg-card'
        }`}
      >
        <AnimatePresence initial={false}>
          {marcada && (
            <motion.span
              initial={{ scale: 0.4, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.4, opacity: 0 }}
              transition={{ type: 'spring', stiffness: 600, damping: 26 }}
            >
              <Check className="h-4 w-4" strokeWidth={3} />
            </motion.span>
          )}
        </AnimatePresence>
      </span>
    </motion.button>
  )
}

/** El interruptor de "Mostrar desayuno y merienda". */
export function MostrarExtras() {
  const { mostrar, alternar } = useMostrarExtras()

  return (
    <button
      type="button"
      role="switch"
      aria-checked={mostrar}
      onClick={alternar}
      className="group inline-flex items-center gap-2.5 rounded-full border border-border/70 bg-card py-1.5 pl-1.5 pr-3.5 text-left outline-none transition-colors hover:border-primary/40 focus-visible:ring-2 focus-visible:ring-ring"
    >
      <span
        aria-hidden="true"
        className={`relative h-5 w-9 shrink-0 rounded-full transition-colors duration-300 ${
          mostrar ? 'bg-sage' : 'bg-border'
        }`}
      >
        <motion.span
          layout
          transition={{ type: 'spring', stiffness: 620, damping: 34 }}
          className={`absolute top-0.5 h-4 w-4 rounded-full bg-card shadow-sm ${
            mostrar ? 'left-[1.125rem]' : 'left-0.5'
          }`}
        />
      </span>
      <span className="label-caps text-muted-foreground transition-colors group-hover:text-foreground">
        Mostrar desayuno y merienda
      </span>
    </button>
  )
}

/** Hueco vacío o mensaje de "aún no hay nada", con borde punteado. */
export function Vacio({ children }: { children: ReactNode }) {
  return (
    <div className="rounded-3xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
      {children}
    </div>
  )
}

/** Un campo con su rótulo encima, en versalitas. */
export function Campo({ id, label, children }: { id?: string; label: string; children: ReactNode }) {
  return (
    <div className="mb-3">
      <label htmlFor={id} className="label-caps mb-1.5 ml-1 block text-muted-foreground">
        {label}
      </label>
      {children}
    </div>
  )
}
